// Reimbursement types
// Import enums from generated file to avoid duplication
import type {
  ApApprovalPattern,
  ApBillStatus,
  PaymentMethodType,
  PublicReimbursementIntakeAccessMode,
  ReimbursementDueDateSource,
  ReimbursementExternalTokenScope,
  ReimbursementFlagType,
  ReimbursementHistoryAction,
  ReimbursementInfoRequestStatus,
  ReimbursementPayoutScheduleAnchor,
  ReimbursementPayoutScheduleMode,
  ReimbursementPayoutScheduleSource,
  ReimbursementPeriodType,
  ReimbursementPolicyEvaluationMode,
  RowStatus,
} from '../generated/enums.js';
import type { ApproverConfig, CreateWorkflowConditionPayload } from '../wf.js';
import type { ResolvedDimensionName } from './classification.js';

// Re-export for convenience
export type {
  PublicReimbursementIntakeAccessMode,
  ReimbursementDueDateSource,
  ReimbursementExternalTokenScope,
  ReimbursementFlagType,
  ReimbursementHistoryAction,
  ReimbursementInfoRequestStatus,
  ReimbursementPayoutScheduleAnchor,
  ReimbursementPayoutScheduleMode,
  ReimbursementPayoutScheduleSource,
  ReimbursementPeriodType,
};

// ============================================================================
// Reimbursement Expense Category (tenant-managed vocabulary)
// ============================================================================

/**
 * Response payload for the tenant-managed reimbursement expense category list/CRUD
 * endpoints (`/ap/reimbursement-expense-categories`). Mirrors the persisted row
 * minus tenant/audit internals that should not leak to the web client.
 */
export interface ReimbursementExpenseCategoryResponse {
  expenseCategoryId: string;
  name: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  status: RowStatus;
  /** Count of active policies referencing this category — surfaced to power the management screen. */
  policyUsageCount?: number;
  /** Count of reimbursement requests whose intake metadata references this category. */
  requestUsageCount?: number;
  createdAt: string;
  updatedAt: string | null;
}

export interface ReimbursementExpenseCategoryListResponse {
  data: ReimbursementExpenseCategoryResponse[];
}

export interface CreateReimbursementExpenseCategoryRequest {
  name: string;
  description?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

export interface UpdateReimbursementExpenseCategoryRequest {
  name?: string;
  description?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

export const ReimbursementIntakePayoutMethod = {
  PIX: 'PIX',
  TED: 'TED',
} as const;
export type ReimbursementIntakePayoutMethod =
  (typeof ReimbursementIntakePayoutMethod)[keyof typeof ReimbursementIntakePayoutMethod];

export const ReimbursementIntakeField = {
  DESCRIPTION: 'DESCRIPTION',
  ATTACHMENTS: 'ATTACHMENTS',
  TOTAL_AMOUNT: 'TOTAL_AMOUNT',
  REQUESTED_AMOUNT: 'REQUESTED_AMOUNT',
  PIX_KEY: 'PIX_KEY',
  COST_CENTER: 'COST_CENTER',
  DEPARTMENT: 'DEPARTMENT',
} as const;
export type ReimbursementIntakeField =
  (typeof ReimbursementIntakeField)[keyof typeof ReimbursementIntakeField];

export const REIMBURSEMENT_INTAKE_FIELDS = [
  ReimbursementIntakeField.DESCRIPTION,
  ReimbursementIntakeField.ATTACHMENTS,
  ReimbursementIntakeField.TOTAL_AMOUNT,
  ReimbursementIntakeField.REQUESTED_AMOUNT,
  ReimbursementIntakeField.PIX_KEY,
  ReimbursementIntakeField.COST_CENTER,
  ReimbursementIntakeField.DEPARTMENT,
] as const satisfies readonly ReimbursementIntakeField[];

export const PublicReimbursementIntakeLinkStatus = {
  ACTIVE: 'ACTIVE',
  REVOKED: 'REVOKED',
} as const;
export type PublicReimbursementIntakeLinkStatus =
  (typeof PublicReimbursementIntakeLinkStatus)[keyof typeof PublicReimbursementIntakeLinkStatus];

export const PublicReimbursementIntakeLinkAvailability = {
  INDEFINITE: 'INDEFINITE',
  ONE_HOUR: 'ONE_HOUR',
  ONE_DAY: 'ONE_DAY',
  FIVE_DAYS: 'FIVE_DAYS',
  THIRTY_DAYS: 'THIRTY_DAYS',
} as const;
export type PublicReimbursementIntakeLinkAvailability =
  (typeof PublicReimbursementIntakeLinkAvailability)[keyof typeof PublicReimbursementIntakeLinkAvailability];

export interface CreatePublicReimbursementIntakeLinkRequest {
  entityId: string;
  description: string;
  availability?: PublicReimbursementIntakeLinkAvailability | null;
  /**
   * Who may request a code on this link. Omitted values keep the historical
   * HCM-employee gate.
   */
  accessMode?: PublicReimbursementIntakeAccessMode | null;
  requiredFields?: ReimbursementIntakeField[] | null;
  expiresAt?: string | null;
}

export interface PublicReimbursementIntakeLinkResponse {
  linkId: string;
  tenantId: string;
  entityId: string;
  description: string;
  slug: string;
  status: PublicReimbursementIntakeLinkStatus;
  accessMode: PublicReimbursementIntakeAccessMode;
  requiredFields: ReimbursementIntakeField[];
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  publicUrl: string | null;
  /** Plaintext token is returned only when the link is created. */
  token?: string;
}

export interface PublicReimbursementIntakeLinkInfoResponse {
  tenantName: string;
  entityId: string;
  requiredFields: ReimbursementIntakeField[];
}

export interface PublicReimbursementIntakeCodeRequest {
  linkToken: string;
  email: string;
  cpfCnpj?: string | null;
}

export interface PublicReimbursementIntakeVerifyRequest {
  linkToken: string;
  email: string;
  code: string;
}

export const ReimbursementApprovalStatus = {
  NO_POLICY_PENDING_REVIEW: 'NO_POLICY_PENDING_REVIEW',
  POLICY_PENDING_APPROVAL: 'POLICY_PENDING_APPROVAL',
  POLICY_APPROVED: 'POLICY_APPROVED',
  AUTO_APPROVED: 'AUTO_APPROVED',
  REJECTED: 'REJECTED',
  MORE_INFO_REQUESTED: 'MORE_INFO_REQUESTED',
  /** Requester answered a manager "more info" request; awaiting manager follow-up. */
  MORE_INFO_RECEIVED: 'MORE_INFO_RECEIVED',
  CONVERTED_TO_BILL: 'CONVERTED_TO_BILL',
  BILL_WORKFLOW_FALLBACK: 'BILL_WORKFLOW_FALLBACK',
} as const;
export type ReimbursementApprovalStatus =
  (typeof ReimbursementApprovalStatus)[keyof typeof ReimbursementApprovalStatus];

export const ReimbursementApprovalOrigin = {
  REIMBURSEMENT_POLICY: 'REIMBURSEMENT_POLICY',
  BILL_GENERIC_WORKFLOW: 'BILL_GENERIC_WORKFLOW',
  MANUAL_NO_POLICY: 'MANUAL_NO_POLICY',
  AUTO_APPROVE: 'AUTO_APPROVE',
  CONVERTED_BILL: 'CONVERTED_BILL',
} as const;
export type ReimbursementApprovalOrigin =
  (typeof ReimbursementApprovalOrigin)[keyof typeof ReimbursementApprovalOrigin];

export const ReimbursementPolicyMatchOrigin = {
  AUTO_MATCH: 'AUTO_MATCH',
  MANAGER_OVERRIDE: 'MANAGER_OVERRIDE',
  NO_MATCH: 'NO_MATCH',
} as const;
export type ReimbursementPolicyMatchOrigin =
  (typeof ReimbursementPolicyMatchOrigin)[keyof typeof ReimbursementPolicyMatchOrigin];

export const ReimbursementPolicyMatchStatus = {
  NO_MATCH: 'NO_MATCH',
  SINGLE_MATCH: 'SINGLE_MATCH',
  AMBIGUOUS_MATCH: 'AMBIGUOUS_MATCH',
  WEAK_MATCH: 'WEAK_MATCH',
} as const;
export type ReimbursementPolicyMatchStatus =
  (typeof ReimbursementPolicyMatchStatus)[keyof typeof ReimbursementPolicyMatchStatus];

export const ReimbursementPolicyDecision = {
  APPROVE_AUTOMATICALLY: 'APPROVE_AUTOMATICALLY',
  REQUEST_APPROVAL: 'REQUEST_APPROVAL',
  REQUEST_MORE_INFO: 'REQUEST_MORE_INFO',
  ALERT_ONLY: 'ALERT_ONLY',
  MANUAL_REVIEW: 'MANUAL_REVIEW',
  REJECT_AUTOMATICALLY: 'REJECT_AUTOMATICALLY',
} as const;
export type ReimbursementPolicyDecision =
  (typeof ReimbursementPolicyDecision)[keyof typeof ReimbursementPolicyDecision];

export const ReimbursementPolicyAlertSeverity = {
  INFO: 'INFO',
  WARNING: 'WARNING',
  BLOCKER: 'BLOCKER',
  REJECT: 'REJECT',
} as const;
export type ReimbursementPolicyAlertSeverity =
  (typeof ReimbursementPolicyAlertSeverity)[keyof typeof ReimbursementPolicyAlertSeverity];

export interface ReimbursementPolicyDecisionAlert {
  code: string;
  severity: ReimbursementPolicyAlertSeverity;
  message: string;
}

export interface ReimbursementPolicyDecisionSnapshot {
  decision: ReimbursementPolicyDecision;
  alerts: ReimbursementPolicyDecisionAlert[];
  blockers: string[];
  reason: string | null;
  evaluatedAt: string;
  origin: ReimbursementPolicyMatchOrigin;
}

export interface ReimbursementRequesterLookupResult {
  source: 'VENDOR' | 'PARTY' | 'NONE';
  vendorId?: string | null;
  partyId?: string | null;
  displayName?: string | null;
}

/** Structured decision from the reimbursement policy AI agent (persisted for audit). */
export const ReimbursementPolicyAiDecision = {
  APPROVED: 'APPROVED',
  APPROVED_WITH_RESERVATION: 'APPROVED_WITH_RESERVATION',
  MANUAL_REVIEW: 'MANUAL_REVIEW',
  REJECTED: 'REJECTED',
} as const;
export type ReimbursementPolicyAiDecision =
  (typeof ReimbursementPolicyAiDecision)[keyof typeof ReimbursementPolicyAiDecision];

export interface ReimbursementAiPolicy {
  /** Tenant-managed category id linked to this policy (preferred matching dimension). */
  expenseCategoryId?: string | null;
  /** Free-text label snapshot kept for backwards compatibility with legacy policies. */
  category?: string | null;
  policyText: string;
}

export interface ReimbursementPolicyDependentEvaluationSnapshot {
  /** True when the policy text or AI evaluation depends on employee dependents. */
  required: boolean;
  /** Dependents the model matched to the request/policy criteria. */
  matchedDependentIds: string[];
  /** Human-readable criteria inferred from the policy text, e.g. "0-5 anos". */
  criteria: string[];
  /** Maximum amount calculated or inferred for the matched dependent criteria. */
  calculatedLimit: number | null;
  /** Missing or ambiguous inputs that prevent automatic approval. */
  missingInformation: string[];
  /** Short explanation of how dependents affected the decision. */
  reason?: string | null;
}

export interface ReimbursementPolicyAiEvaluationSnapshot {
  decision: ReimbursementPolicyAiDecision;
  confidence: number;
  flags?: string[];
  internalReason?: string | null;
  /** Short message for requester or UI when rejected or flagged. */
  publicMessage?: string | null;
  approvedAmount?: number | null;
  reservationNote?: string | null;
  /** Prefix of sha256(ai policy text at evaluation time). */
  policyTextHash?: string | null;
  evaluatedAt?: string | null;
  modelId?: string | null;
  /** True when the model output was invalid or the call failed and review was forced. */
  parseFallback?: boolean;
  dependentEvaluation?: ReimbursementPolicyDependentEvaluationSnapshot | null;
}

export interface ReimbursementDocumentReuseReference {
  documentId: string;
  originalName?: string | null;
  previousRequestIds: string[];
  existingBillId?: string | null;
  detectedAt: string;
  /** Prior reimbursement requests that already reached an approved or paid state. */
  priorApprovedRequestIds?: string[];
  /** Prior AP bills (linked to the reused document) that are approved or paid. */
  priorApprovedBillIds?: string[];
}

export const ReimbursementAutoRejectionReason = {
  DUPLICATE_DOCUMENT_ALREADY_APPROVED: 'DUPLICATE_DOCUMENT_ALREADY_APPROVED',
} as const;
export type ReimbursementAutoRejectionReason =
  (typeof ReimbursementAutoRejectionReason)[keyof typeof ReimbursementAutoRejectionReason];

export interface ReimbursementAutoRejectionSnapshot {
  reason: ReimbursementAutoRejectionReason;
  comment: string;
  priorApprovedRequestIds: string[];
  priorApprovedBillIds: string[];
  rejectedAt: string;
}

export const ReimbursementEntitySuggestionStatus = {
  PENDING: 'PENDING',
  KEPT_DEFAULT: 'KEPT_DEFAULT',
  ACCEPTED: 'ACCEPTED',
} as const;
export type ReimbursementEntitySuggestionStatus =
  (typeof ReimbursementEntitySuggestionStatus)[keyof typeof ReimbursementEntitySuggestionStatus];

/** Entity recommendation retained when intake deliberately keeps the configured default. */
export interface ReimbursementEntitySuggestion {
  entityId: string;
  confidence: number;
  evidence: string;
  status: ReimbursementEntitySuggestionStatus;
  suggestedAt: string;
  decidedByUserId?: string | null;
  decidedAt?: string | null;
}

export interface ReimbursementIntakeMetadata {
  /**
   * High-level expense bucket chosen by the requester at intake (marketing, events, etc.),
   * distinct from AP bill classification / dimension review. Persisted as a label snapshot;
   * once {@link expenseCategoryId} is populated it becomes the authoritative reference.
   */
  expenseClassification?: string | null;
  /**
   * Tenant-managed expense category id resolved from {@link expenseClassification} at
   * intake submit (or set explicitly by the new combobox). Enables deterministic policy
   * matching by UUID instead of string equality.
   */
  expenseCategoryId?: string | null;
  /** How the requester expects to receive payment (Pix or bank transfer). */
  payoutMethod?: ReimbursementIntakePayoutMethod | null;
  pixKey?: string | null;
  /** TED / bank transfer details when {@link payoutMethod} is TED. */
  bankCode?: string | null;
  bankAgency?: string | null;
  bankAccountNumber?: string | null;
  bankAccountDigit?: string | null;
  bankAccountType?: 'CHECKING' | 'SAVINGS' | null;
  description?: string | null;
  merchantVendorId?: string | null;
  merchantName?: string | null;
  merchantTaxId?: string | null;
  tags?: string[] | null;
  customFields?: Record<string, unknown> | null;
  reviewClassification?: ReimbursementReviewClassification | null;
  aiExtraction?: ReimbursementAiExtractionSnapshot | null;
  departmentId?: string | null;
  costCenterId?: string | null;
  attachmentCount?: number | null;
  cpfCnpj?: string | null;
  email?: string | null;
  name?: string | null;
  /** Access mode of the public link that created this request, when it came from one. */
  publicIntakeAccessMode?: PublicReimbursementIntakeAccessMode | null;
  /**
   * False when a public or company-domain requester typed a CPF/CNPJ that was
   * not matched to the verified email. Downstream linking must ignore that tax id.
   */
  requesterTaxIdVerified?: boolean | null;
  requesterLookup?: ReimbursementRequesterLookupResult | null;
  /** Set when a master approver rejects a no-policy intake that never had an approval chain. */
  manualManagerOutcome?: 'REJECTED' | null;
  manualRejectionComment?: string | null;
  manualRejectionAt?: string | null;
  manualRejectedByUserId?: string | null;
  /** Set when the platform rejects a request without manager action (e.g. duplicate approved receipt). */
  autoRejection?: ReimbursementAutoRejectionSnapshot | null;
  /**
   * Set when a manager explicitly reopens a rejected request for a fresh manual review
   * (ALC-1050). Superseded by any subsequent auto/manual rejection.
   */
  reopenedForReview?: ReimbursementReopenedForReviewSnapshot | null;
  /** Reviewer-proposed approval amount before final approve (≤ requested); stored until approval or cleared. */
  reviewApprovedAmount?: number | null;
  policyMatch?: ReimbursementPolicyMatchSnapshot | null;
  policyLimit?: ReimbursementPolicyLimitEvaluation | null;
  /** Latest AI policy evaluation (when the matched policy uses evaluation mode AI). */
  policyAiEvaluation?: ReimbursementPolicyAiEvaluationSnapshot | null;
  /** Normalized policy decision after matching, including guardrail blockers. */
  policyDecision?: ReimbursementPolicyDecisionSnapshot | null;
  /** Documents in this request whose bytes had already appeared in previous reimbursement requests. */
  documentReuseWarnings?: ReimbursementDocumentReuseReference[] | null;
  /**
   * Ranked policy candidates when auto-match did not apply a single policy.
   * Populated after intake evaluation for manager review.
   */
  policySuggestions?: ReimbursementPolicySuggestionsSnapshot | null;
  /** Shared id when multiple requests were submitted in one intake batch (ALC-825). */
  intakeSubmissionId?: string | null;
  /** 1-based position within {@link intakeSubmissionCount}. */
  intakeSubmissionIndex?: number | null;
  /** Total requests in the same {@link intakeSubmissionId} batch. */
  intakeSubmissionCount?: number | null;
  /** Public portal path for intake tracking (`/reimbursements/respond/:token`). */
  intakeTrackingUrl?: string | null;
  /** Set when the requester withdraws the reimbursement via the public tracking portal. */
  requesterCancelledAt?: string | null;
  requesterCancellationNote?: string | null;
  /** Human decision required before approval/conversion when status is PENDING. */
  entitySuggestion?: ReimbursementEntitySuggestion | null;
  [key: string]: unknown;
}

export type ReimbursementPolicySuggestionConfidence = 'HIGH' | 'MEDIUM' | 'LOW';

export type ReimbursementPolicyCategoryAlignment =
  'ID_MATCH' | 'LABEL_MATCH' | 'AI_INFERRED' | 'NONE';

export interface ReimbursementPolicySuggestion {
  policyId: string;
  policyName: string;
  evaluationMode: ReimbursementPolicyEvaluationMode;
  score: number;
  confidence: ReimbursementPolicySuggestionConfidence;
  /** i18n keys for UI (e.g. POLICY_SUGGEST_CATEGORY_ID_MATCH). */
  reasonCodes: string[];
  predictedDecision?: ReimbursementPolicyDecision | null;
  categoryAlignment: ReimbursementPolicyCategoryAlignment;
}

export interface ReimbursementPolicySuggestionsSnapshot {
  evaluatedAt: string;
  recommendedPolicyId: string | null;
  candidates: ReimbursementPolicySuggestion[];
  autoApplyEligible: boolean;
  noMatchReason?: string | null;
}

export interface ReimbursementReviewClassification {
  departmentId?: string | null;
  costCenterId?: string | null;
  /** Internal GL account suggestion reused when converting the reimbursement to a bill. */
  glAccountId?: string | null;
  /** Internal managerial account suggestion reused when converting the reimbursement to a bill. */
  managerialAccountId?: string | null;
  segments?: Record<string, string | null> | null;
  customFields?: Record<string, unknown> | null;
  tags?: string[] | null;
  source?: 'AI_SUGGESTED' | 'REVIEWER_CONFIRMED' | 'MANUAL' | null;
  confidence?: number | null;
}

/** Parsed health-plan billing breakdown (e.g. Unimed demonstrativo de faturamento). */
export interface ReimbursementHealthPlanBeneficiarySnapshot {
  memberNumber: string | null;
  name: string;
  planCode: string | null;
  userType: 'TITULAR' | 'DEPENDENTE' | 'AGREGADO' | 'UNKNOWN';
  birthDate: string | null;
  age: number | null;
  includedAt: string | null;
  amount: number;
}

export interface ReimbursementHealthPlanBillingSnapshot {
  monthlyFee: number;
  coparticipation: number | null;
  totalBilled: number | null;
  /** Number of beneficiaries when itemized rows are intentionally redacted from a response. */
  beneficiaryCount?: number | null;
  /** Itemized beneficiaries shown in family/company health-plan statements. */
  beneficiaries?: ReimbursementHealthPlanBeneficiarySnapshot[] | null;
  source: 'document_text';
}

export interface ReimbursementAiExtractionSnapshot {
  source?: string | null;
  attachmentCount?: number | null;
  intakeMode?: 'single' | 'multiple' | string | null;
  merchantName?: string | null;
  merchantTaxId?: string | null;
  totalAmount?: number | null;
  requestedAmount?: number | null;
  expenseDate?: string | null;
  /** Payment due / expected reimbursement date suggestion (YYYY-MM-DD). */
  reimbursementDueDate?: string | null;
  /** When the model surfaced a payout key (e.g. some receipts). */
  pixKey?: string | null;
  description?: string | null;
  categoryLabel?: string | null;
  /**
   * Resolved tenant category id when {@link categoryLabel} was mapped to the
   * pre-registered vocabulary (exact or fuzzy match). Omitted when unresolved.
   */
  expenseCategoryId?: string | null;
  tags?: string[] | null;
  confidence?: number | null;
  /**
   * Requester (payer) details inferred from the document's `parties.customer`. Populated
   * only when the customer is plausibly a person/employee — i.e. **not** the tenant entity
   * itself. When the customer's tax id matches the entity CNPJ, the extraction service
   * drops these fields to avoid suggesting "the company" as the requester. The service
   * also prefers PF (CPF) data; CNPJ customers other than the entity are still surfaced
   * because they may be a distinct corporate filer (rare on reimbursements).
   */
  requesterName?: string | null;
  requesterTaxId?: string | null;
  requesterEmail?: string | null;
  /**
   * When the receipt is a health-plan statement, separates monthly fee from
   * coparticipation so intake can prefill reimbursable amounts.
   */
  healthPlanBilling?: ReimbursementHealthPlanBillingSnapshot | null;
  raw?: Record<string, unknown> | null;
}

/** Response body for synchronous intake attachment extraction (before form submit). */
export interface ReimbursementAiExtractionResponse {
  snapshot: ReimbursementAiExtractionSnapshot;
  /** Full model output and metadata for audit (persist under {@link ReimbursementAiExtractionSnapshot.raw}). */
  raw: Record<string, unknown>;
}

/** Per-file analysis status surfaced to the client. */
export type ReimbursementAiPerFileStatus = 'ready' | 'failed';

/**
 * Single document analyzed by {@link ReimbursementAiBatchExtractionResponse}.
 * `index` matches the position the client sent in `files[]` so the UI can map
 * back to the original {@link File} objects.
 */
export interface ReimbursementAiPerFileAnalysis {
  index: number;
  fileName: string;
  mimeType: string;
  status: ReimbursementAiPerFileStatus;
  /** Populated when status === 'ready'; null when extraction failed for this file only. */
  snapshot: ReimbursementAiExtractionSnapshot | null;
  /** Populated when status === 'ready'; agent payload + meta retained for audit. */
  raw?: Record<string, unknown> | null;
  /** Short message when status === 'failed'. */
  errorMessage?: string | null;
}

/**
 * One inferred reimbursement request. The grouping engine bins per-file analyses
 * by document signature (vendor + amount + date) and emits one entry per bin.
 */
export interface ReimbursementAiInferredGroup {
  /** Stable id within this response (e.g. `g0`, `g1`). */
  groupId: string;
  /** Indices into {@link ReimbursementAiBatchExtractionResponse.files}. */
  fileIndices: number[];
  /** Aggregated snapshot for the files in this group. */
  snapshot: ReimbursementAiExtractionSnapshot;
}

/**
 * AI's recommendation about whether the uploaded files belong to one request
 * or to several. The user is expected to confirm or override this.
 */
export interface ReimbursementAiGroupingRecommendation {
  mode: 'single' | 'multiple';
  /** 0..1 confidence score for the recommendation. */
  confidence: number;
  /** Short human-readable justification (pt-BR), used by the UI. */
  reasoning: string;
  /** Inferred grouping (length 1 when mode === 'single'). */
  groups: ReimbursementAiInferredGroup[];
}

/**
 * Response body for the per-document extraction + grouping endpoint introduced
 * by ALC-711. Replaces (super-set of) {@link ReimbursementAiExtractionResponse}
 * for the multi-document UX. The legacy single-snapshot endpoint is kept for
 * backwards compatibility while ALC-690 callers migrate.
 */
export interface ReimbursementAiBatchExtractionResponse {
  /** Per-file analysis results, in the order the files were sent. */
  files: ReimbursementAiPerFileAnalysis[];
  /** Aggregated snapshot used when the user picks `single` mode. */
  aggregatedSnapshot: ReimbursementAiExtractionSnapshot;
  /** AI grouping recommendation; the user can override it. */
  recommendation: ReimbursementAiGroupingRecommendation;
  /** Audit-only payload (model meta, latencies, raw extractions). */
  raw: Record<string, unknown>;
}

/** Limits shared by API endpoints (`FilesInterceptor`) and web client copy. */
export const REIMBURSEMENT_AI_EXTRACTION_MAX_FILES = 10;
export const REIMBURSEMENT_AI_EXTRACTION_MAX_FILE_BYTES = 10 * 1024 * 1024;

/** Error code returned by both extraction endpoints in the 502 body. */
export const REIMBURSEMENT_AI_EXTRACTION_FAILED_CODE = 'AI_EXTRACTION_FAILED' as const;
export type ReimbursementAiExtractionErrorCode = typeof REIMBURSEMENT_AI_EXTRACTION_FAILED_CODE;

export interface ReimbursementPolicyResponse {
  policyId: string;
  tenantId: string;
  entityId: string | null;
  name: string;
  description: string | null;
  isActive: boolean;
  evaluationMode: ReimbursementPolicyEvaluationMode;
  aiPolicy: {
    expenseCategoryId: string | null;
    category: string | null;
    policyText: string | null;
  } | null;
  workflowId: string | null;
  priority: number;
  specificityScore: number;
  maxAmountPerTransaction: string | null; // Decimal as string
  maxAmountPerPeriod: string | null;
  periodType: ReimbursementPeriodType | null;
  periodLimitPerRequester: boolean; // true = per requester, false = global
  autoApproveUpTo: string | null;
  requiresBusinessJustification: boolean;
  justificationInstructions: string | null;
  maxExpenseAgeDays: number | null;
  payoutScheduleSource: ReimbursementPayoutScheduleSource;
  payoutScheduleMode: ReimbursementPayoutScheduleMode | null;
  payoutScheduleDays: number[] | null;
  payoutScheduleOffsetDays: number | null;
  payoutScheduleAnchor: ReimbursementPayoutScheduleAnchor | null;
  createdAt: string;
  updatedAt: string | null;
  /**
   * Workflow details needed to rehydrate the policy form.
   */
  conditions?: CreateWorkflowConditionPayload[];
  approvalSteps?: ApprovalStepPayload[];
  autoApprove?: boolean;
}

export interface ReimbursementPolicyLimitEvaluation {
  perTransactionCap: number | null;
  periodRemainingCap: number | null;
  effectiveLimit: number | null;
  requestedAmount: number;
  reviewerProposedAmount: number | null;
  approvedAmount: number;
  /** True when {@link ApplyReimbursementPolicyRequest.allowLimitOverride} allowed a value above the effective cap. */
  limitOverrideApplied?: boolean;
  periodUsedAmount?: number | null;
  periodRequestCount?: number | null;
}

export interface ReimbursementPolicyMatchSnapshot {
  origin: ReimbursementPolicyMatchOrigin;
  status?: ReimbursementPolicyMatchStatus;
  policyId: string | null;
  workflowId: string | null;
  matched: boolean;
  reason: string | null;
  evaluatedAt: string;
  matchedPolicyIds?: string[];
}

export const ReimbursementPolicyErrorCode = {
  ENTITY_REQUIRED: 'REIMBURSEMENT_POLICY_ENTITY_REQUIRED',
  WORKFLOW_ENTITY_MISMATCH: 'REIMBURSEMENT_POLICY_WORKFLOW_ENTITY_MISMATCH',
} as const;
export type ReimbursementPolicyErrorCode =
  (typeof ReimbursementPolicyErrorCode)[keyof typeof ReimbursementPolicyErrorCode];

export interface CreateReimbursementPolicyPayload {
  entityId?: string | null;
  name: string;
  description?: string | null;
  isActive?: boolean;
  evaluationMode?: ReimbursementPolicyEvaluationMode;
  aiPolicy?: ReimbursementAiPolicy | null;
  workflowId?: string | null;
  priority?: number;
  maxAmountPerTransaction?: string | null;
  maxAmountPerPeriod?: string | null;
  periodType?: ReimbursementPeriodType | null;
  periodLimitPerRequester?: boolean;
  autoApproveUpTo?: string | null;
  requiresBusinessJustification?: boolean;
  justificationInstructions?: string | null;
  maxExpenseAgeDays?: number | null;
  payoutScheduleSource?: ReimbursementPayoutScheduleSource;
  payoutScheduleMode?: ReimbursementPayoutScheduleMode | null;
  payoutScheduleDays?: number[] | null;
  payoutScheduleOffsetDays?: number | null;
  payoutScheduleAnchor?: ReimbursementPayoutScheduleAnchor | null;
  conditions?: CreateWorkflowConditionPayload[];
  approvalSteps?: ApprovalStepPayload[];
  autoApprove?: boolean;
}

export interface UpdateReimbursementPolicyPayload {
  entityId?: string | null;
  name?: string;
  description?: string | null;
  isActive?: boolean;
  evaluationMode?: ReimbursementPolicyEvaluationMode;
  aiPolicy?: ReimbursementAiPolicy | null;
  workflowId?: string | null;
  priority?: number;
  maxAmountPerTransaction?: string | null;
  maxAmountPerPeriod?: string | null;
  periodType?: ReimbursementPeriodType | null;
  periodLimitPerRequester?: boolean;
  autoApproveUpTo?: string | null;
  requiresBusinessJustification?: boolean;
  justificationInstructions?: string | null;
  maxExpenseAgeDays?: number | null;
  payoutScheduleSource?: ReimbursementPayoutScheduleSource;
  payoutScheduleMode?: ReimbursementPayoutScheduleMode | null;
  payoutScheduleDays?: number[] | null;
  payoutScheduleOffsetDays?: number | null;
  payoutScheduleAnchor?: ReimbursementPayoutScheduleAnchor | null;
  conditions?: CreateWorkflowConditionPayload[];
  approvalSteps?: ApprovalStepPayload[];
  autoApprove?: boolean;
}

export interface ApprovalStepPayload {
  name?: string;
  approvalPattern: ApApprovalPattern;
  approvers: ApproverConfig[];
  /** Segregation of duties: exclude bill requester from approvers (default false). */
  excludeBillRequester?: boolean;
}

export interface ReimbursementHistoryEntryResponse {
  historyId: string;
  action: ReimbursementHistoryAction;
  performedByUserId: string | null;
  performedByName: string | null;
  notes: string | null;
  changes: Record<string, unknown> | null;
  createdAt: string;
}

export interface ReimbursementRequestResponse {
  requestId: string;
  tenantId: string;
  entityId: string;
  /** Set when the request is linked to an AP bill; intake submissions start without a bill. */
  billId: string | null;
  /** Status of the linked AP bill, when one has been created from this request. */
  billStatus: ApBillStatus | null;
  approvalStatus: ReimbursementApprovalStatus;
  approvalOrigin: ReimbursementApprovalOrigin | null;
  approvalStatusReason: string | null;
  pendingStepInstanceId: string | null;
  approvalChainId: string | null;
  requesterEmployeeId: string | null;
  requesterVendorId: string | null;
  /** Resolved payee name (HCM employee / AP vendor outrank intake / receipt text). */
  requesterDisplayName: string | null;
  originalAmount: number;
  requestedAmount: number;
  policyLimitAmount: number | null;
  approvedAmount: number | null;
  currencyCode: string;
  expenseDate: string;
  reimbursementDueDate: string | null;
  reimbursementDueDateSource: ReimbursementDueDateSource | null;
  reimbursementDueDateRuleSnapshot: Record<string, unknown> | null;
  paymentMethod: PaymentMethodType | null;
  bankAccountId: string | null;
  originBankAccountId: string | null;
  businessJustification: string | null;
  description: string | null;
  merchantVendorId: string | null;
  merchantName: string | null;
  merchantTaxId: string | null;
  tags: string[];
  customFields: Record<string, unknown> | null;
  reviewClassification: ReimbursementReviewClassification | null;
  resolvedDepartmentId: string | null;
  resolvedCostCenterId: string | null;
  /** Resolved names for the ids above, mirroring the bills pending-approval read's shape. Null when unclassified. */
  resolvedDepartment?: ResolvedDimensionName | null;
  resolvedCostCenter?: ResolvedDimensionName | null;
  policyId: string | null;
  policyMatch: ReimbursementPolicyMatchSnapshot | null;
  policyLimit: ReimbursementPolicyLimitEvaluation | null;
  intakeMetadata: ReimbursementIntakeMetadata | null;
  /** Warning codes clients must acknowledge before an approval/conversion action. */
  approvalWarnings?: string[];
  flags: ReimbursementFlagType[];
  aiDetectionReasoning: string | null;
  createdAt: string;
  updatedAt: string | null;
  /** Audit trail for this request (ordered oldest → newest when returned by list/detail). */
  history?: ReimbursementHistoryEntryResponse[];
}

export interface PendingReimbursementApprovalItemResponse {
  request: ReimbursementRequestResponse;
  chainId: string;
  stepInstanceId: string;
  stepNumber: number;
  stepName: string;
  assignedToUserIds: string[];
  startedAt: string;
  createdAt: string;
}

export interface PendingReimbursementApprovalResponse {
  data: PendingReimbursementApprovalItemResponse[];
  nextCursor: string | null;
  total: number;
}

export interface CreateReimbursementRequestPayload {
  billId: string;
  entityId: string;
  requesterEmployeeId?: string | null;
  requesterVendorId?: string | null;
  originalAmount: number;
  requestedAmount: number;
  approvedAmount?: number | null;
  currencyCode?: string | null;
  expenseDate: string;
  reimbursementDueDate?: string | null;
  paymentMethod?: PaymentMethodType | null;
  bankAccountId?: string | null;
  originBankAccountId?: string | null;
  businessJustification?: string | null;
  /** Requester-visible classification label; persisted on {@link ReimbursementIntakeMetadata}. */
  expenseClassification?: string | null;
  policyId?: string | null;
  intakeMetadata?: ReimbursementIntakeMetadata | null;
  flags?: ReimbursementFlagType[];
  aiDetectionReasoning?: string | null;
}

export interface CreateReimbursementIntakeRequest {
  entityId: string;
  cpfCnpj: string;
  email: string;
  /**
   * Required: tenant-managed category label for the expense. May be a newly typed value
   * (the API will resolve / create the matching {@link ReimbursementExpenseCategoryResponse}
   * when needed). Kept as a label snapshot in `intakeMetadata.expenseClassification`.
   */
  expenseClassification: string;
  /**
   * Optional: tenant-managed expense category id. When provided, the matcher uses it as the
   * primary dimension to identify an applicable policy. When omitted, the API attempts to
   * resolve it from `expenseClassification` against the tenant's active categories.
   */
  expenseCategoryId?: string | null;
  name?: string | null;
  description?: string | null;
  totalAmount: number;
  requestedAmount: number;
  currencyCode?: string | null;
  expenseDate: string;
  /**
   * Solicitation intake (manager + public POST) ignores this — the API sets the payout date
   * automatically (typically D+7 from submission). Managers may change it in review via
   * {@link UpdateReimbursementReviewRequest.reimbursementDueDate}.
   */
  reimbursementDueDate?: string | null;
  payoutMethod?: ReimbursementIntakePayoutMethod | null;
  pixKey?: string | null;
  bankCode?: string | null;
  bankAgency?: string | null;
  bankAccountNumber?: string | null;
  bankAccountDigit?: string | null;
  bankAccountType?: 'CHECKING' | 'SAVINGS' | null;
  merchantVendorId?: string | null;
  merchantName?: string | null;
  merchantTaxId?: string | null;
  tags?: string[] | null;
  customFields?: Record<string, unknown> | null;
  reviewClassification?: ReimbursementReviewClassification | null;
  aiExtraction?: ReimbursementAiExtractionSnapshot | null;
  departmentId?: string | null;
  costCenterId?: string | null;
  attachmentCount?: number | null;
  businessJustification?: string | null;
  requesterEmployeeId?: string | null;
  requesterVendorId?: string | null;
  intakeMetadata?: ReimbursementIntakeMetadata | null;
}

export interface UpdateReimbursementReviewRequest {
  /** Legal entity confirmed by the reviewer; accepting the stored suggestion resolves its warning. */
  entityId?: string;
  merchantVendorId?: string | null;
  merchantName?: string | null;
  merchantTaxId?: string | null;
  requesterEmployeeId?: string | null;
  requesterVendorId?: string | null;
  tags?: string[] | null;
  customFields?: Record<string, unknown> | null;
  reviewClassification?: ReimbursementReviewClassification | null;
  /** Expected reimbursement payout date (YYYY-MM-DD); omit to leave unchanged. */
  reimbursementDueDate?: string | null;
  /**
   * Amount the reviewer intends to approve (must be ≤ requested). Omitted = unchanged; null =
   * clear proposal (defaults to full requested on approval).
   */
  approvedAmount?: number | null;
}

export interface ApplyReimbursementPolicyRequest {
  policyId: string | null;
  /** Optional manager note explaining manual policy selection or clearing. */
  comment?: string | null;
  /** Defaults to false; when true, value above policy limit is recorded as an explicit override. */
  allowLimitOverride?: boolean;
}

/** Audit snapshot recorded when a manager reopens a rejected reimbursement request. */
export interface ReimbursementReopenedForReviewSnapshot {
  reopenedAt: string;
  reopenedByUserId: string;
  comment: string | null;
}

export interface ReopenReimbursementRequestRequest {
  /** Optional manager note explaining why the rejection is being reopened. */
  comment?: string | null;
}

/** How an HCM employee was matched as a reimbursement requester/beneficiary. */
export const ReimbursementRequesterMatchSource = {
  EMAIL: 'EMAIL',
  CPF: 'CPF',
} as const;
export type ReimbursementRequesterMatchSource =
  (typeof ReimbursementRequesterMatchSource)[keyof typeof ReimbursementRequesterMatchSource];

/**
 * Request body for `POST /ap/reimbursement-requests/resolve-requester`. At least
 * one of {@link email} / {@link cpfCnpj} should be provided.
 */
export interface ResolveReimbursementRequesterRequest {
  email?: string | null;
  cpfCnpj?: string | null;
}

/**
 * Resolved HCM employee match for a reimbursement requester/beneficiary, or
 * `null` when no unambiguous active employee matches. Email match is preferred;
 * a unique CPF match is used as fallback.
 */
export interface ResolveReimbursementRequesterResponse {
  employeeId: string;
  requesterName: string;
  cpfCnpj: string | null;
  matchSource: ReimbursementRequesterMatchSource;
}

/** Response from POST /ap/reimbursement-requests/intake (same shape as {@link ReimbursementRequestResponse}). */
export type ReimbursementIntakeResponse = ReimbursementRequestResponse;

/**
 * Single sub-request in {@link CreateReimbursementIntakeBatchRequest}. Fields
 * mirror {@link CreateReimbursementIntakeRequest} except for the per-batch
 * `clientGroupId` used by the client to route uploaded attachments to the
 * matching created request.
 */
export interface CreateReimbursementIntakeBatchItem extends CreateReimbursementIntakeRequest {
  /**
   * Stable client-side id (matches {@link ReimbursementAiInferredGroup.groupId}
   * when the user accepted the AI grouping). Echoed back on the response so
   * the client can map files → created request.
   */
  clientGroupId: string;
}

/**
 * Body for `POST /ap/reimbursement-requests/intake/batch` (manager flow).
 * Creates N {@link ReimbursementRequestResponse} rows in one call. Attachments
 * are still uploaded separately via `/ap/documents` per-request.
 */
export interface CreateReimbursementIntakeBatchRequest {
  /**
   * Mode the user submitted with. The API only validates that `requests.length`
   * matches; persisted snapshots already encode the per-request grouping.
   */
  intakeMode: 'single' | 'multiple';
  requests: CreateReimbursementIntakeBatchItem[];
}

/** Response from `POST /ap/reimbursement-requests/intake/batch`. */
export interface CreateReimbursementIntakeBatchResponse {
  /** Created requests in submission order. `clientGroupId` echoes the input. */
  requests: (ReimbursementIntakeResponse & { clientGroupId: string })[];
}

/** Body for the public batch-submit endpoint (mirrors the manager batch payload). */
export interface PublicReimbursementIntakeSubmitBatchRequest {
  intakeToken: string;
  intakeMode: 'single' | 'multiple';
  requests: CreateReimbursementIntakeBatchItem[];
}

/**
 * Response from `POST /public/reimbursements/intake/submit-batch`.
 * Each created request carries its own short-lived attachment upload token
 * when {@link CreateReimbursementIntakeBatchItem.attachmentCount} > 0.
 */
export interface PublicReimbursementIntakeSubmitBatchResponse {
  requests: (ReimbursementIntakeResponse & {
    clientGroupId: string;
    attachmentUploadToken?: string;
    trackingToken?: string;
    trackingUrl?: string;
  })[];
}

/**
 * Response from POST /public/reimbursements/intake/submit.
 * When {@link CreateReimbursementIntakeRequest.attachmentCount} is greater than zero, the API returns
 * {@link PublicReimbursementIntakeSubmitResponse.attachmentUploadToken} for a follow-up multipart upload.
 */
export type PublicReimbursementIntakeSubmitResponse = ReimbursementIntakeResponse & {
  attachmentUploadToken?: string;
  trackingToken?: string;
  trackingUrl?: string;
};

// ============================================================================
// Public "more info" portal (ALC-800)
// ============================================================================

/**
 * Per-attachment summary surfaced to the requester on the public portal.
 * Mirrors {@link ReimbursementAiExtractionSnapshot.attachmentCount} fields
 * but stays safe to expose externally — only filename/mime/size/uploadedAt,
 * no internal blob path or storage URL.
 */
export interface PublicReimbursementMoreInfoAttachment {
  documentId: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  uploadedAt: string;
}

export interface PublicReimbursementMoreInfoMessage {
  messageId: string;
  senderRole: 'MANAGER' | 'REQUESTER';
  senderName: string | null;
  message: string | null;
  changedFields: string[];
  attachmentCount: number | null;
  createdAt: string;
}

export type PublicReimbursementTimelineEventType =
  | 'REQUEST_SUBMITTED'
  | 'POLICY_EVALUATED'
  | 'AMOUNT_ADJUSTED'
  | 'INFO_REQUESTED'
  | 'INFO_RESPONDED'
  | 'APPROVED'
  | 'REJECTED'
  | 'CONVERTED_TO_BILL'
  | 'UPDATED'
  | 'CANCELLED';

export interface PublicReimbursementTimelineEntry {
  entryId: string;
  eventType: PublicReimbursementTimelineEventType;
  occurredAt: string;
  actorName: string | null;
  message: string | null;
  changedFields: string[];
  attachmentCount: number | null;
  approvedAmount: number | null;
  expectedPaymentDate: string | null;
  currencyCode: string | null;
}

/**
 * Snapshot of the current reimbursement state shown to the external
 * requester. Pre-populated into the response form so the user only edits
 * what's missing/wrong.
 */
/** One row in a multi-request intake submission shown on the public tracking portal. */
export interface PublicReimbursementIntakeSubmissionSummaryItem {
  requestIndex: number;
  requestId: string;
  expenseDate: string;
  totalAmount: number;
  requestedAmount: number;
  currencyCode: string;
  expenseClassification: string | null;
  merchantName: string | null;
  reimbursementDueDate: string | null;
  /** Relative path (`/reimbursements/respond/:token`) for this request. */
  trackingUrl: string | null;
  /** True when this row matches the token currently open in the portal. */
  isCurrent: boolean;
}

/** Aggregated view of every request in the same intake batch submission. */
export interface PublicReimbursementIntakeSubmissionSummary {
  submissionId: string;
  requestCount: number;
  totalRequestedAmount: number;
  totalReceiptAmount: number;
  currencyCode: string;
  items: PublicReimbursementIntakeSubmissionSummaryItem[];
}

/** Distinguishes intake tracking links from manager "more info" prompts. */
export type PublicReimbursementPortalKind = 'TRACKING' | 'MORE_INFO';

/** Self-service actions available on the public portal (tracking or pending more-info). */
export interface PublicReimbursementRequesterActions {
  canEdit: boolean;
  canCancel: boolean;
  canUploadAttachments: boolean;
  /** When all actions are blocked, the same machine reason used for `canRespond` / modification gates. */
  blockedReason: string | null;
}

/** Machine codes for receipt checks surfaced on the public tracking portal and manager dashboard. */
export const ReimbursementIntakeReceiptAlertCode = {
  RECEIPT_TOTAL_EXCEEDS_REQUESTED: 'RECEIPT_TOTAL_EXCEEDS_REQUESTED',
  REQUESTER_NAME_MISSING_ON_RECEIPT: 'REQUESTER_NAME_MISSING_ON_RECEIPT',
  REQUESTER_NAME_MISMATCH_ON_RECEIPT: 'REQUESTER_NAME_MISMATCH_ON_RECEIPT',
} as const;

export type ReimbursementIntakeReceiptAlertCode =
  (typeof ReimbursementIntakeReceiptAlertCode)[keyof typeof ReimbursementIntakeReceiptAlertCode];

export interface ReimbursementIntakeReceiptAlert {
  code: ReimbursementIntakeReceiptAlertCode;
}

export interface PublicReimbursementMoreInfoRequestSnapshot {
  /** Reimbursement request id (echoed for client diagnostics; never tenant-scoped). */
  requestId: string;
  /** Date the expense occurred (YYYY-MM-DD). */
  expenseDate: string;
  /** Original receipt total (currency-coded). */
  totalAmount: number;
  /** Amount being requested for reimbursement (≤ total). */
  requestedAmount: number;
  currencyCode: string;
  /** Expected reimbursement payment date (YYYY-MM-DD) when set at intake. */
  reimbursementDueDate?: string | null;
  description: string | null;
  businessJustification: string | null;
  expenseClassification: string | null;
  /** Tenant-managed category id when resolved at intake or more-info response. */
  expenseCategoryId?: string | null;
  merchantName: string | null;
  merchantTaxId: string | null;
  pixKey: string | null;
  /** Display name of the original requester (employee/contractor). */
  requesterName: string | null;
  requesterCpfCnpj: string | null;
  requesterEmail: string | null;
  attachments: PublicReimbursementMoreInfoAttachment[];
}

/** Response from GET /public/reimbursements/more-info/status. */
export interface PublicReimbursementMoreInfoStatusResponse {
  /** Tenant display name (already i18n-safe). */
  tenantName: string;
  /** Entity display name when resolvable. */
  entityName: string | null;
  /** Manager-authored message rendered above the form. */
  message: string;
  /** Display name of the manager who asked for the info (already-redacted form). */
  requestedBy: string | null;
  /** Hint about which fields the manager wants completed. Free-form list of canonical field codes. */
  requestedFields: string[];
  /** Lifecycle of the info request. */
  status: ReimbursementInfoRequestStatus;
  issuedAt: string;
  expiresAt: string;
  respondedAt: string | null;
  /** True when the token is still usable AND the underlying reimbursement is in a state that accepts a response. */
  canRespond: boolean;
  /** When `canRespond` is false, a human-readable reason ("token expired", "request already approved", ...). */
  blockedReason: string | null;
  /** Chronological manager/requester message thread for this info request. */
  messageHistory: PublicReimbursementMoreInfoMessage[];
  /** Public-safe full reimbursement timeline for requester follow-up. */
  timeline: PublicReimbursementTimelineEntry[];
  /** Snapshot of the current reimbursement fields, pre-filled into the form. */
  request: PublicReimbursementMoreInfoRequestSnapshot;
  /**
   * Present when this request was part of a multi-request intake batch: lists every
   * pedido in the same envio with per-item amounts and tracking links.
   */
  submissionSummary?: PublicReimbursementIntakeSubmissionSummary | null;
  /** Whether this token is the post-intake tracking link or a manager info-request link. */
  portalKind: PublicReimbursementPortalKind;
  /** Edit / cancel / attach capabilities independent of the manager more-info lifecycle. */
  requesterActions: PublicReimbursementRequesterActions;
  /**
   * Short-lived token for `POST /upload-attachments` while {@link PublicReimbursementRequesterActions.canUploadAttachments}
   * is true. Refreshed on each status load.
   */
  attachmentUploadToken?: string | null;
  /**
   * Non-blocking receipt quality hints from intake AI (amount vs comprovante, beneficiary name).
   * Empty when no issues were detected or extraction was unavailable.
   */
  receiptAlerts: ReimbursementIntakeReceiptAlert[];
}

/**
 * Payload for POST /public/reimbursements/more-info/respond.
 * Every field is optional: when omitted, the existing value is preserved.
 * Sending `null` for a nullable field clears it; sending a string sets it.
 */
export interface PublicReimbursementMoreInfoRespondRequest {
  description?: string | null;
  businessJustification?: string | null;
  expenseClassification?: string | null;
  /** Tenant-managed category id when the requester picks from the catalog. */
  expenseCategoryId?: string | null;
  merchantName?: string | null;
  merchantTaxId?: string | null;
  pixKey?: string | null;
  /** Free-form note from the requester explaining what changed (logged in history). */
  responseNote?: string | null;
}

/** Response from POST /public/reimbursements/more-info/respond. */
export interface PublicReimbursementMoreInfoRespondResponse {
  status: ReimbursementInfoRequestStatus;
  respondedAt: string;
  /** Short-lived token for follow-up `POST /upload-attachments` when extra files are needed. */
  attachmentUploadToken?: string;
  /** Refreshed status payload so the UI can render the "received" state without a second round-trip. */
  snapshot: PublicReimbursementMoreInfoStatusResponse;
}

/** Payload for POST /public/reimbursements/more-info/update (tracking portal self-service). */
export type PublicReimbursementPortalUpdateRequest = PublicReimbursementMoreInfoRespondRequest;

/** Response from POST /public/reimbursements/more-info/update. */
export interface PublicReimbursementPortalUpdateResponse {
  attachmentUploadToken?: string;
  snapshot: PublicReimbursementMoreInfoStatusResponse;
}

/** Payload for POST /public/reimbursements/more-info/cancel. */
export interface PublicReimbursementPortalCancelRequest {
  cancellationNote?: string | null;
}

/** Response from POST /public/reimbursements/more-info/cancel. */
export interface PublicReimbursementPortalCancelResponse {
  snapshot: PublicReimbursementMoreInfoStatusResponse;
}

/** Response from POST /public/reimbursements/intake/verify-code */
export interface PublicReimbursementIntakeVerifyResponse {
  intakeToken: string;
  entityId: string;
  tenantName: string;
  requiredFields: ReimbursementIntakeField[];
  /** Email that completed OTP verification; should be treated as read-only on the form. */
  requesterEmail: string;
  /** Requester display name from HCM / allowlist / account, when available. */
  requesterName: string | null;
  /** Normalized tax id digits when resolved at code verification time. */
  requesterCpfCnpj: string | null;
  /** Linked HCM employee when resolved at verification time. */
  requesterEmployeeId: string | null;
}
